import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Users, Heart, ChevronDown, ChevronUp } from 'lucide-react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage, StructuredProgress } from '../types';
import { ProgressDisplay } from './progress';
import PlanReviewCard from '../../../components/chat/PlanReviewCard';
import {
  APPROVE_PLAN_MESSAGE,
  planEtaRunCopy,
  resolveRunEta,
} from '../../../components/chat/planReview';
import type { PlanReviewPayload } from '../../../components/chat/planReview';

interface PersonaCited {
  id: number;
  name?: string;
  bio?: string;
}

interface Props {
  messages: ChatMessage[];
  isSending: boolean;
  progress: string;
  structuredProgress: StructuredProgress | null;
  onSendMessage: (query: string) => void;
  hasActiveSession: boolean;
}

const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-gray-900 mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-gray-900 mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => (
    <h3 className="text-base font-semibold text-gray-900 mt-3 mb-1">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-gray-900 mt-2 mb-1">{children}</h4>
  ),
  p: ({ children }) => <p className="text-sm text-gray-700 mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="text-sm text-gray-700 leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#E8FCFA] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">
        {children}
      </code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-[#07E5D1] hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-gray-200" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#E8FCFA]">{children}</thead>,
  tbody: ({ children }) => <tbody className="bg-white divide-y divide-gray-100">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-gray-50 transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children }) => <td className="px-4 py-3 text-sm text-gray-700 align-top">{children}</td>,
};

const PersonaTooltip: React.FC<{
  persona: PersonaCited;
  children: React.ReactNode;
}> = ({ persona, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 320;
      const margin = 20;

      let x = rect.left + rect.width / 2;
      const minX = margin + tooltipWidth / 2;
      const maxX = window.innerWidth - margin - tooltipWidth / 2;
      x = Math.max(minX, Math.min(maxX, x));

      setPosition({ x, y: rect.top - 8 });
    }
    setIsVisible(true);
  };

  const initials = (persona.name || `P${persona.id}`)
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsVisible(false)}
      className="relative inline-block"
    >
      {children}
      {isVisible && (
        <div
          className="fixed transform -translate-x-1/2 -translate-y-full pointer-events-none"
          style={{ left: position.x, top: position.y, zIndex: 9999 }}
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 min-w-[280px] max-w-[320px] mb-2">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#07E5D1] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-sm">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-900">
                  {persona.name || `Persona ${persona.id}`}
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">Maternal Healthcare Persona</p>
              </div>
            </div>
            {persona.bio && (
              <p className="text-sm text-gray-600 leading-relaxed">
                {persona.bio.length > 180 ? persona.bio.slice(0, 180) + '...' : persona.bio}
              </p>
            )}
            {!persona.bio && <p className="text-sm text-gray-400 italic">Bio not available</p>}
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-3 h-3 bg-white border-r border-b border-gray-200 transform rotate-45" />
        </div>
      )}
    </div>
  );
};

const PersonasAccordion: React.FC<{ personas: PersonaCited[] }> = ({ personas }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleCount = 4;
  const hasMore = personas.length > visibleCount;
  const visiblePersonas = isExpanded ? personas : personas.slice(0, visibleCount);
  const hiddenCount = personas.length - visibleCount;

  if (personas.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-gray-400" />
        <span className="text-xs text-gray-500 font-medium">
          {personas.length} persona{personas.length !== 1 ? 's' : ''} contributed to this response
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {visiblePersonas.map((persona) => (
          <PersonaTooltip key={persona.id} persona={persona}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full hover:border-[#07E5D1] hover:bg-[#E8FCFA] transition-all cursor-pointer group">
              <div className="w-5 h-5 rounded-full bg-[#07E5D1] flex items-center justify-center text-white text-[9px] font-medium">
                {(persona.name || `P${persona.id}`)
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .slice(0, 2)}
              </div>
              <span className="text-xs text-gray-700 group-hover:text-[#07E5D1] font-medium">
                {persona.name || `Persona ${persona.id}`}
              </span>
            </div>
          </PersonaTooltip>
        ))}

        {hasMore && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs text-gray-600 font-medium">Show less</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs text-gray-600 font-medium">+{hiddenCount} more</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export const ChatInterface: React.FC<Props> = ({
  messages,
  isSending,
  progress,
  structuredProgress,
  onSendMessage,
  hasActiveSession,
}) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, progress]);

  useEffect(() => {
    if (hasActiveSession) {
      inputRef.current?.focus();
    }
  }, [hasActiveSession]);

  const handleSend = () => {
    if (!input.trim() || isSending) return;
    setApprovedPlanReview(null); // a typed send supersedes any approved plan's ETA
    onSendMessage(input.trim());
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Plan review (metadata.plan_review) ─────────────────────────────
  // Approve = run the plan exactly as shown; edits = a normal chat message
  // carrying the composed change description (the backend resumes/replans).
  // Both go through onSendMessage — the SAME path as a typed message.
  // Plan-review ETA pinning: the progress area shows the approved plan's ETA
  // only for runs actually started from the card (see handlePlanApprove).
  const [approvedPlanReview, setApprovedPlanReview] = useState<PlanReviewPayload | null>(null);
  const lastPlanReviewRef = useRef<PlanReviewPayload | null>(null);

  const handlePlanApprove = () => {
    // Pin the approved plan's ETA to THIS run — the last assistant message's
    // plan_review would show a stale estimate if the user typed a new question instead.
    setApprovedPlanReview(lastPlanReviewRef.current);
    onSendMessage(APPROVE_PLAN_MESSAGE);
  };
  const handlePlanRequestChanges = (message: string) => {
    setApprovedPlanReview(null);
    onSendMessage(message);
  };
  const focusComposer = () => {
    inputRef.current?.focus();
  };

  if (!hasActiveSession) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#FEFEFE]">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 rounded-full bg-[#07E5D1] flex items-center justify-center text-white mx-auto mb-6 shadow-lg">
            <Heart className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Healthcare Persona Chat</h2>
          <p className="text-gray-500 mb-6">
            Select a conversation from the sidebar or start a new one to chat with our panel of
            maternal healthcare personas.
          </p>
          <button
            onClick={() => onSendMessage('')}
            className="px-6 py-3 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl font-medium transition-colors shadow-sm"
          >
            Start New Chat
          </button>
        </div>
      </div>
    );
  }

  // Plan-derived ETA for the in-flight run: prefer a live structured_progress
  // ETA; else the ETA of the plan the user just approved (the plan card is the
  // latest assistant message exactly when the current run was started from it).
  const lastAssistantMsg = [...messages].reverse().find((m) => m.role === 'assistant');
  const runEta = resolveRunEta(structuredProgress, approvedPlanReview ?? undefined);
  lastPlanReviewRef.current = lastAssistantMsg?.metadata?.plan_review ?? null;
  const runEtaCopy = planEtaRunCopy(runEta?.eta_minutes_min, runEta?.eta_minutes_max);

  return (
    <div className="flex-1 flex flex-col bg-[#FEFEFE]">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">
          {messages.length === 0 && !isSending && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-[#07E5D1] flex items-center justify-center text-white mx-auto mb-6 shadow-lg">
                <Heart className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Ask AI Personas</h2>
              <p className="text-gray-500 max-w-lg mx-auto mb-2">
                Ask any question about maternal healthcare experiences. The AI will intelligently
                select relevant personas and synthesize their insights.
              </p>
              <p className="text-sm text-[#07E5D1] font-medium mb-8">
                Maternal Healthcare Insights
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  "Compare Asian vs Black mothers' hospital experiences",
                  "What are first-time moms' biggest fears?",
                  'How do mothers feel about epidurals?',
                ].map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(suggestion)}
                    className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 hover:border-[#07E5D1] hover:text-[#07E5D1] transition-colors shadow-sm"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-6">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (msg.metadata?.plan_review?.steps?.length ?? 0) > 0 ? (
                  // The plan card replaces the bubble (msg.content is a markdown
                  // fallback of the same plan). Only the LATEST message is
                  // actionable; older cards render read-only — superseded plans.
                  <PlanReviewCard
                    steps={msg.metadata!.plan_review!.steps}
                    etaMinutesMin={msg.metadata!.plan_review!.eta_minutes_min}
                    etaMinutesMax={msg.metadata!.plan_review!.eta_minutes_max}
                    onApprove={handlePlanApprove}
                    onRequestChanges={handlePlanRequestChanges}
                    onFocusComposer={focusComposer}
                    disabled={isSending || idx !== messages.length - 1}
                  />
                ) : (
                  <div
                    className={`max-w-[85%] rounded-2xl px-5 py-4 ${
                      msg.role === 'user'
                        ? 'bg-[#07E5D1] text-white rounded-br-md'
                        : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <>
                        <div className="max-w-none prose-table:w-full">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={markdownComponents}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                        {msg.metadata?.personas_cited && msg.metadata.personas_cited.length > 0 && (
                          <PersonasAccordion personas={msg.metadata.personas_cited} />
                        )}
                      </>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                )}
              </div>
            ))}

            {isSending && (
              <div className="flex justify-start">
                <ProgressDisplay
                  progress={structuredProgress}
                  fallback={progress || 'Processing...'}
                  etaCopy={runEtaCopy}
                />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      <div className="bg-white border-t border-gray-100 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about maternal healthcare experiences..."
              disabled={isSending}
              className="flex-1 px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/20 focus:border-[#07E5D1] focus:bg-white transition-all disabled:bg-gray-100 disabled:text-gray-500"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isSending}
              className="p-3.5 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isSending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-3">
            Powered by AI • Insights from Maternal Healthcare Personas
          </p>
        </div>
      </div>
    </div>
  );
};
